import { phoneToE164 } from './phone.js';
const CENTS = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
const WHOLE_DOLLARS = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
});
/**
 * The office's money, as the office writes it: `$1,234.50`, or `$1,235` with
 * `wholeDollars` for a tile or a card where cents are noise. A negative amount
 * reads `-$20.00`. The one formatter — every screen used to carry its own copy.
 */
export function formatCurrency(amount, options = {}) {
    const value = Number.isFinite(amount) ? amount : 0;
    return (options.wholeDollars ? WHOLE_DOLLARS : CENTS).format(value);
}
export function formatDate(date, locale = 'he-IL') {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
    }).format(d);
}
/**
 * Format a 24h "HH:MM" time string as 12h AM/PM ("13:30" → "1:30PM") —
 * the same display used across timeline views. Non-standard values are
 * returned as-is; null/undefined become an empty string.
 */
export function formatTimeDisplay(time) {
    if (!time)
        return '';
    const match = time.match(/^(\d{1,2}):(\d{2})$/);
    if (match) {
        const h = parseInt(match[1]);
        const m = match[2];
        return `${h % 12 || 12}:${m}${h >= 12 ? 'PM' : 'AM'}`;
    }
    return time;
}
/**
 * Build a WhatsApp chat link (wa.me) from a free-form phone number. A number
 * that reads as a real one (`phoneToE164`, US by default) is used as read;
 * otherwise formatting is stripped and a 10-digit number assumed US/CA (+1).
 * Returns null when the number is missing or too short to be dialable.
 */
export function buildWhatsAppUrl(phone) {
    if (!phone)
        return null;
    const e164 = phoneToE164(phone);
    if (e164)
        return `https://wa.me/${e164.slice(1)}`;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8)
        return null;
    const withCountry = digits.length === 10 ? `1${digits}` : digits;
    return `https://wa.me/${withCountry}`;
}
export function formatDateShort(date, locale = 'he-IL') {
    const d = typeof date === 'string' ? new Date(date) : date;
    return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).format(d);
}
/**
 * Capitalize the first letter of the first word of free text — used to tidy
 * user-typed names/titles (timeline entries, sections, item names, …) both on
 * save and on display.
 *
 * The rest of the string is left untouched (so intentional inner casing and
 * acronyms survive), and the operation is idempotent. If the first word is a
 * tag placeholder (e.g. `{GROOM}`), it is left exactly as-is so tag codes stay
 * fully uppercase — leading whitespace is likewise preserved.
 */
export function capitalizeFirstWord(text) {
    if (!text)
        return '';
    const firstCharMatch = text.match(/\S/);
    if (!firstCharMatch || firstCharMatch.index === undefined)
        return text;
    const idx = firstCharMatch.index;
    // Leading tag placeholder ({CODE}) — never alter it.
    if (text[idx] === '{')
        return text;
    return text.slice(0, idx) + text[idx].toUpperCase() + text.slice(idx + 1);
}
//# sourceMappingURL=formatters.js.map